'use client';

import Link from 'next/link';
import Image from 'next/image';
import { ArrowRight } from 'lucide-react';
import { PROJECT_NAME } from '@shared/constants';
import { LogoPath1, LogoPath2 } from '@shared/ui/src/components/icons';

interface HeroSectionProps {
  onScrollToHowItWorks: () => void;
}

/**
 * @group Landing
 * @summary Hero section with main heading, badges, and primary CTAs
 * 
 * UPDATED 23/01/26
 */
export function HeroSection({ onScrollToHowItWorks }: HeroSectionProps) {

  return (
    <section className="relative min-h-[calc(100vh-120px)] flex flex-col">
      {/* Main Content Area */}
      <div className="bg-hero-bg px-4 sm:px-8 lg:px-24 py-16 min-h-[calc(100vh-120px)] flex items-center">
        <div className="max-w-7xl mx-auto w-full">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left: Content */}
            <div className="text-center lg:text-left">
              {/* Main Heading - Serif font */}
              <h1 className="text-4xl sm:text-5xl lg:text-6xl mb-6 text-hero-text font-medium leading-tight font-serif">
                <span className="lg:whitespace-nowrap">Quality Care in Your Pocket</span>{' '}
                <span className="block text-brand-teal lg:whitespace-nowrap">Anywhere in South Africa</span>
              </h1>

              {/* Description */}
              <p className="text-lg sm:text-xl text-[#B8B4B0] mb-8 leading-relaxed max-w-2xl mx-auto lg:mx-0">
                Phila is an AI subscription-based digital health platform providing remote medical consultations by our full-time employed HPCSA-registered practitioners. The platform improves healthcare access and continuity of care across South Africa and the world, including underserved and rural populations, via secure mobile technology.
              </p>

              {/* CTAs */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <Link
                  href="/users/signup"
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-primary text-primary-foreground rounded-lg font-semibold hover:bg-primary/90 transition-colors"
                >
                  Book a Consult <ArrowRight className="w-5 h-5" />
                </Link>
                <button
                  onClick={onScrollToHowItWorks}
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 border-2 border-primary text-primary rounded-lg font-semibold hover:bg-secondary/20 transition-colors"
                >
                  How {PROJECT_NAME} Works
                </button>
              </div>
            </div>

            {/* Right: Stacked Images with Logo Path Masks */}
            <div className="flex flex-col gap-0">
              {/* Top Image - Masked by LogoPath1, aligned right */}
              <div className="w-[85%] self-end">
                <svg
                  className="w-full h-auto"
                  viewBox="178 83 280 185"
                  preserveAspectRatio="xMidYMid meet"
                >
                  <defs>
                    <clipPath id="logoPath1Clip">
                      <path d="M389.81,259.67c31.57-8.5,53.31-34.76,59.04-70.06,4.28-26.37-4.52-53.69-23.56-72.95-33.84-34.24-89.85-35.36-125.1-2.49l-116.18,108.3c-1.48,1.38-.77,3.87,1.22,4.23,16.75,3.03,33.51,7.78,50.35,11.11,17.93,3.55,35.84,7.21,53.7,11.1,5.72,1.24,11.43,2.51,17.14,3.8,3.54.8,7.09,1.6,10.63,2.4,23.59,5.29,48.79,11.01,72.76,4.56Z" />
                    </clipPath>
                  </defs>
                  <image
                    href="/img/doctor.webp"
                    x="170"
                    y="90"
                    width="300"
                    height="350"
                    clipPath="url(#logoPath1Clip)"
                    preserveAspectRatio="xMidYMid slice"
                  />
                  <path
                    d="M389.81,259.67c31.57-8.5,53.31-34.76,59.04-70.06,4.28-26.37-4.52-53.69-23.56-72.95-33.84-34.24-89.85-35.36-125.1-2.49l-116.18,108.3c-1.48,1.38-.77,3.87,1.22,4.23,16.75,3.03,33.51,7.78,50.35,11.11,17.93,3.55,35.84,7.21,53.7,11.1,5.72,1.24,11.43,2.51,17.14,3.8,3.54.8,7.09,1.6,10.63,2.4,23.59,5.29,48.79,11.01,72.76,4.56Z"
                    fill="none"
                    stroke="white"
                    strokeWidth="2"
                  />
                </svg>
              </div>
              
              {/* Bottom Image - Masked by LogoPath2, aligned left */}
              <div className="w-[85%] self-start lg:-mt-12 lg:-ml-16">
                <svg
                  className="w-full h-auto"
                  viewBox="43 233 280 185"
                  preserveAspectRatio="xMidYMid meet"
                >
                  <defs>
                    <clipPath id="logoPath2Clip">
                      <path d="M113.98,240.33c-31.57,8.5-53.31,34.76-59.04,70.06-4.28,26.37,4.52,53.69,23.56,72.95,33.84,34.24,89.85,35.36,125.1,2.49,2.22-2.07,4.43-4.13,6.65-6.2,5.72-5.33,11.43-10.66,17.15-15.99,7.82-7.29,15.64-14.58,23.46-21.87,8.53-7.95,17.06-15.9,25.59-23.85,7.84-7.31,15.69-14.62,23.53-21.93,5.76-5.37,11.52-10.74,17.28-16.11.02-.02.04-.04.06-.06,2.23-2.08,1.41-5.79-1.49-6.7-.08-.02-.16-.05-.23-.07-3.55-1-7.1-1.94-10.67-2.85-7.91-2-15.89-3.72-23.89-5.33-21.94-4.41-43.86-8.56-65.7-13.5-9.53-2.16-19.06-4.34-28.6-6.48-23.59-5.29-48.79-11.01-72.76-4.56Z" />
                    </clipPath>
                  </defs>
                  <g clipPath="url(#logoPath2Clip)">
                    <image
                      href="/img/patient.webp"
                      x="180"
                      y="190"
                      width="300"
                      height="350"
                      preserveAspectRatio="xMidYMid slice"
                      transform="translate(503.78, 0) scale(-1, 1)"
                    />
                  </g>
                  <path
                    d="M113.98,240.33c-31.57,8.5-53.31,34.76-59.04,70.06-4.28,26.37,4.52,53.69,23.56,72.95,33.84,34.24,89.85,35.36,125.1,2.49,2.22-2.07,4.43-4.13,6.65-6.2,5.72-5.33,11.43-10.66,17.15-15.99,7.82-7.29,15.64-14.58,23.46-21.87,8.53-7.95,17.06-15.9,25.59-23.85,7.84-7.31,15.69-14.62,23.53-21.93,5.76-5.37,11.52-10.74,17.28-16.11.02-.02.04-.04.06-.06,2.23-2.08,1.41-5.79-1.49-6.7-.08-.02-.16-.05-.23-.07-3.55-1-7.1-1.94-10.67-2.85-7.91-2-15.89-3.72-23.89-5.33-21.94-4.41-43.86-8.56-65.7-13.5-9.53-2.16-19.06-4.34-28.6-6.48-23.59-5.29-48.79-11.01-72.76-4.56Z"
                    fill="none"
                    stroke="white"
                    strokeWidth="2"
                  />
                </svg>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
